import { Controller } from "@hotwired/stimulus"

/**
 * Theme Controller - 管理亮色/暗色主题切换
 *
 * 用法:
 *   <div data-controller="theme">
 *     <button data-action="click->theme#toggle">切换主题</button>
 *     <!-- 或者使用具体的方法 -->
 *     <button data-action="click->theme#setLight">亮色</button>
 *     <button data-action="click->theme#setDark">暗色</button>
 *     <button data-action="click->theme#setSystem">跟随系统</button>
 *   </div>
 *
 * 主题状态存储在 localStorage 的 'theme' key 中:
 *   - 'light': 强制亮色
 *   - 'dark': 强制暗色
 *   - null/undefined: 跟随系统
 */
export default class extends Controller {
  static targets = ["iconLight", "iconDark", "iconSystem"]
  static values = {
    default: { type: String, default: "system" } // 'light', 'dark', 'system'
  }

  connect() {
    this.applyTheme()

    // 监听系统主题变化
    this.mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
    this.mediaQuery.addEventListener("change", this.handleSystemChange.bind(this))
  }

  disconnect() {
    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener("change", this.handleSystemChange.bind(this))
    }
  }

  /**
   * 切换主题: light -> dark -> system -> light
   */
  toggle() {
    const current = this.currentTheme()

    if (current === "light") {
      this.setDark()
    } else if (current === "dark") {
      this.setSystem()
    } else {
      this.setLight()
    }
  }

  /**
   * 设置为亮色主题
   */
  setLight() {
    localStorage.setItem("theme", "light")
    this.applyTheme()
    this.dispatch("changed", { detail: { theme: "light" } })
  }

  /**
   * 设置为暗色主题
   */
  setDark() {
    localStorage.setItem("theme", "dark")
    this.applyTheme()
    this.dispatch("changed", { detail: { theme: "dark" } })
  }

  /**
   * 设置为跟随系统
   */
  setSystem() {
    localStorage.removeItem("theme")
    this.applyTheme()
    this.dispatch("changed", { detail: { theme: "system" } })
  }

  /**
   * 获取当前主题设置
   * @returns {'light' | 'dark' | 'system'}
   */
  currentTheme() {
    return localStorage.getItem("theme") || "system"
  }

  /**
   * 获取当前实际应用的主题 (考虑系统偏好)
   * @returns {'light' | 'dark'}
   */
  effectiveTheme() {
    const theme = this.currentTheme()

    if (theme === "system") {
      return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
    }

    return theme
  }

  /**
   * 应用主题到 DOM
   */
  applyTheme() {
    const theme = this.currentTheme()
    const html = document.documentElement

    // 移除所有主题类
    html.classList.remove("light", "dark")

    if (theme === "light") {
      html.classList.add("light")
    } else if (theme === "dark") {
      html.classList.add("dark")
    }
    // 'system' 时不添加类，让 CSS media query 生效

    // 更新 meta theme-color (可选，用于移动端浏览器)
    this.updateMetaThemeColor()
    this.updateIcons(theme)
  }

  /**
   * 处理系统主题变化
   */
  handleSystemChange(event) {
    // 只有在 'system' 模式下才响应
    if (this.currentTheme() === "system") {
      this.dispatch("changed", {
        detail: {
          theme: "system",
          effectiveTheme: event.matches ? "dark" : "light"
        }
      })
      this.updateMetaThemeColor()
      this.updateIcons("system")
    }
  }

  /**
   * 更新 meta theme-color
   */
  updateMetaThemeColor() {
    const effective = this.effectiveTheme()
    const color = effective === "dark" ? "#18181b" : "#ffffff" // zinc-900 / white

    let meta = document.querySelector('meta[name="theme-color"]')
    if (!meta) {
      meta = document.createElement("meta")
      meta.name = "theme-color"
      document.head.appendChild(meta)
    }
    meta.content = color
  }

  /**
   * 根据当前主题显示对应图标
   */
  updateIcons(theme) {
    if (!this.hasIconLightTarget || !this.hasIconDarkTarget || !this.hasIconSystemTarget) return

    const show = (target, visible) => {
      target.classList.toggle("hidden", !visible)
    }

    show(this.iconLightTarget, theme === "light")
    show(this.iconDarkTarget, theme === "dark")
    show(this.iconSystemTarget, theme === "system")
  }
}
